import {
  Tooltip, TooltipTrigger, TooltipContent,
} from "@/components/ui/tooltip";

/**
 * Iter127 — Shared metric-breakdown tooltip cells.
 *
 * These four hover-explain components are used on the manager Leaderboard
 * table AND on the rep's own Dashboard "My Standing" panel, so reps can
 * understand *exactly* how every number was computed without pinging a
 * manager. Each component renders the value with a dotted-underline cue;
 * hovering pops a Radix Tooltip with the math, source breakdown, and any
 * contextual gotchas.
 *
 * Every component expects `rep` to be a leaderboard row from
 * `/api/leaderboard`, which carries these payload fields:
 *   shows, no_shows, deals, cash, sets, show_percent, close_percent,
 *   financial_dqs_taken, cash_breakdown, sets_breakdown,
 *   sets_breakdown_labels, has_eod_activity, user_id.
 *
 * IMPORTANT: any consumer MUST wrap the parent container in a
 * `<TooltipProvider>` once. The components themselves don't render one
 * (cheaper, and lets a single provider control delay across the whole
 * page).
 */

const fmtCurrency = (v) => `$${Number(v).toLocaleString()}`;

export function ShowPercentCell({ rep }) {
  if (rep.show_percent === null || rep.show_percent === undefined) {
    return <span className="text-yellow-400">—</span>;
  }
  const shows = Number(rep.shows || 0);
  const noShows = Number(rep.no_shows || 0);
  const callsTaken = shows + noShows;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="text-yellow-400 cursor-help underline decoration-dotted decoration-yellow-400/40 underline-offset-4"
          data-testid={`show-pct-trigger-${rep.user_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {rep.show_percent}%
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="end"
        className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2 text-xs max-w-[260px]"
        data-testid={`show-pct-tooltip-${rep.user_id}`}
      >
        <div className="font-semibold text-emerald-300 mb-1.5">Show % breakdown</div>
        <div className="space-y-0.5 font-mono text-[11px]">
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-400">Shows</span>
            <span className="text-white tabular-nums">{shows}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-400">+ No-shows</span>
            <span className="text-rose-300 tabular-nums">{noShows}</span>
          </div>
          <div className="border-t border-white/10 my-1.5"></div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-300">{shows} ÷ {callsTaken} =</span>
            <span className="text-yellow-300 font-semibold tabular-nums">{rep.show_percent}%</span>
          </div>
        </div>
        <div className="mt-1.5 text-[10px] text-gray-500 leading-tight">
          Calls taken = shows + no-shows. Cancelled calls are excluded so
          last-minute cancels don&apos;t drop the rep&apos;s show rate.
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function CashCell({ rep }) {
  const total = Number(rep.cash || 0);
  const b = rep.cash_breakdown || {};
  const closerCash = Number(b.webhook_closer || 0);
  const setterCash = Number(b.webhook_setter || 0);
  const eodCash = Number(b.eod || 0);
  const webhookTotal = closerCash + setterCash;
  if (total === 0 && webhookTotal === 0 && eodCash === 0) {
    return <span className="text-green-400 font-medium">{fmtCurrency(total)}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="text-green-400 font-medium cursor-help underline decoration-dotted decoration-green-400/40 underline-offset-4"
          data-testid={`cash-trigger-${rep.user_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {fmtCurrency(total)}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="end"
        className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2 text-xs max-w-[280px]"
        data-testid={`cash-tooltip-${rep.user_id}`}
      >
        <div className="font-semibold text-emerald-300 mb-1.5">Cash sources</div>
        <div className="space-y-0.5 font-mono text-[11px]">
          {(closerCash > 0 || setterCash > 0) && (
            <>
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-400">From webhooks</span>
                <span className="text-white tabular-nums">{fmtCurrency(webhookTotal)}</span>
              </div>
              {closerCash > 0 && (
                <div className="flex items-center justify-between gap-3 pl-3">
                  <span className="text-gray-500">└ as closer</span>
                  <span className="text-gray-300 tabular-nums">{fmtCurrency(closerCash)}</span>
                </div>
              )}
              {setterCash > 0 && (
                <div className="flex items-center justify-between gap-3 pl-3">
                  <span className="text-gray-500">└ as setter</span>
                  <span className="text-gray-300 tabular-nums">{fmtCurrency(setterCash)}</span>
                </div>
              )}
            </>
          )}
          {eodCash > 0 && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-400">From EOD reports</span>
              <span className="text-white tabular-nums">{fmtCurrency(eodCash)}</span>
            </div>
          )}
          <div className="border-t border-white/10 my-1.5"></div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-300">Total (MAX)</span>
            <span className="text-green-300 font-semibold tabular-nums">{fmtCurrency(total)}</span>
          </div>
        </div>
        {webhookTotal > 0 && eodCash > 0 && (
          <div className="mt-1.5 text-[10px] text-gray-500 leading-tight">
            We take MAX of webhook vs EOD-reported cash so reps who log in
            both sources aren&apos;t double-counted.
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export function SetsCell({ rep }) {
  const total = Number(rep.sets || 0);
  const breakdown = rep.sets_breakdown || {};
  const labels = rep.sets_breakdown_labels || {};
  const sources = Object.entries(breakdown)
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
  if (rep.has_eod_activity === false) {
    return <span className="text-gray-600">—</span>;
  }
  if (sources.length === 0) {
    return <span className="text-white">{total}</span>;
  }
  const labelFor = (k) => {
    if (labels[k]) return labels[k];
    if (k === "sets") return "Sets (direct)";
    return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="text-white cursor-help underline decoration-dotted decoration-white/30 underline-offset-4"
          data-testid={`sets-trigger-${rep.user_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {total}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="end"
        className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2 text-xs max-w-[280px]"
        data-testid={`sets-tooltip-${rep.user_id}`}
      >
        <div className="font-semibold text-emerald-300 mb-1.5">Sets sources</div>
        <div className="space-y-0.5 font-mono text-[11px]">
          {sources.slice(0, 8).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <span className="text-gray-400 truncate" title={key}>
                {labelFor(key)}
              </span>
              <span className="text-white tabular-nums">{Number(value).toLocaleString()}</span>
            </div>
          ))}
          {sources.length > 8 && (
            <div className="text-[10px] text-gray-500 italic">
              + {sources.length - 8} more source{sources.length - 8 === 1 ? "" : "s"}
            </div>
          )}
          <div className="border-t border-white/10 my-1.5"></div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-300">Total</span>
            <span className="text-emerald-300 font-semibold tabular-nums">{total}</span>
          </div>
        </div>
        {sources.length > 1 && (
          <div className="mt-1.5 text-[10px] text-gray-500 leading-tight">
            Multiple template fields alias to <span className="text-gray-400">Sets</span> —
            we sum them so custom-template users count alongside default-template users.
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export function ClosePercentCell({ rep }) {
  if (rep.close_percent === null || rep.close_percent === undefined) {
    return <span className="text-yellow-400">—</span>;
  }
  const deals = Number(rep.deals || 0);
  const shows = Number(rep.shows || 0);
  const finDqs = Number(rep.financial_dqs_taken || 0);
  const needsMapping = rep.needs_field_mapping === true;
  // Iter176 — no more `max(shows, deals)` fabrication. Denominator is
  // strictly `max(shows − financial_dqs_taken, deals)` when shows > 0;
  // otherwise close_rate is 0 AND the server flags needs_field_mapping
  // so the tooltip can point the rep at the root cause.
  const closableShows = shows > 0 ? Math.max(shows - finDqs, deals) : 0;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="text-yellow-400 cursor-help underline decoration-dotted decoration-yellow-400/40 underline-offset-4"
          data-testid={`close-pct-trigger-${rep.user_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {rep.close_percent}%
          {needsMapping && (
            <span className="ml-1 text-amber-400" title="EOD shows field unmapped">⚠</span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="end"
        className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2 text-xs max-w-[280px]"
        data-testid={`close-pct-tooltip-${rep.user_id}`}
      >
        <div className="font-semibold text-emerald-300 mb-1.5">Close % breakdown</div>
        <div className="space-y-0.5 font-mono text-[11px]">
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-400">Deals</span>
            <span className="text-orange-300 tabular-nums">{deals}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-400">Shows (mapped)</span>
            <span className="text-white tabular-nums">{shows}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-400">− Financial DQs</span>
            <span className="text-rose-300 tabular-nums">{finDqs}</span>
          </div>
          <div className="border-t border-white/10 my-1.5"></div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-300">{deals} ÷ {closableShows} =</span>
            <span className="text-yellow-300 font-semibold tabular-nums">{rep.close_percent}%</span>
          </div>
        </div>
        {needsMapping && (
          <div className="mt-2 px-2 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 text-[10px] text-amber-200 leading-tight">
            ⚠ This rep has {deals} deal{deals === 1 ? "" : "s"} but 0 mapped shows.
            Their custom EOD &quot;shows&quot; field isn&apos;t wired to the canonical
            column — ask a manager to open <span className="font-semibold">Map EOD Fields</span> to fix.
          </div>
        )}
        {!needsMapping && finDqs > 0 && shows - finDqs < deals && (
          <div className="mt-1.5 text-[10px] text-gray-500 leading-tight">
            Denominator floored at <span className="text-gray-400">deals</span> so close % never exceeds 100%.
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
